import { DragEvent, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Search,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";

import { leaveApi } from "../api";
import { useToast } from "#/components/common/ToastProvider";
import { getErrorMessage } from "../hooks";
import { MODULE_LABELS, MODULE_ORDER, normaliseEligible } from "../types";
import type {
  ApprovalModule,
  CurrentEmployee,
  DelegateCandidateOption,
  EligibleLeaveType,
  LeaveApplyRequest,
  ReliefEmployeeOption,
} from "../types";

// ─── helpers ──────────────────────────────────────────────────────────────────

function isWeekend(d: Date) {
  const n = d.getDay();
  return n === 0 || n === 6;
}
function addDays(d: Date, n: number) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function toISO(d: Date) {
  return d.toISOString().slice(0, 10);
}
function todayIso() {
  return toISO(new Date());
}
function fmt(date: string) {
  try {
    return new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return date;
  }
}
function countDays(start: string, end: string, excludeWeekends: boolean) {
  if (!start || !end || end < start) return 0;
  let cur = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  let n = 0;
  while (cur <= endDate) {
    if (!excludeWeekends || !isWeekend(cur)) n++;
    cur = addDays(cur, 1);
  }
  return n;
}
function calcMaxEnd(start: string, maxDays: number, excludeWeekends: boolean) {
  if (!start || maxDays <= 0) return "";
  let cur = new Date(`${start}T00:00:00`);
  let counted = 0;
  while (counted < maxDays) {
    if (!excludeWeekends || !isWeekend(cur)) counted++;
    if (counted >= maxDays) break;
    cur = addDays(cur, 1);
  }
  return toISO(cur);
}

/** Returns true when [aStart, aEnd] overlaps [bStart, bEnd]. */
function datesOverlap(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
  return aStart <= bEnd && aEnd >= bStart;
}

const ACCEPTED_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];
const ACCEPTED_EXT = ".pdf,.png,.jpg,.jpeg";

// ─── types ────────────────────────────────────────────────────────────────────

type UploadState =
  | { status: "idle" }
  | { status: "uploading"; name: string }
  | { status: "done"; name: string; path: string }
  | { status: "error"; message: string };

// ─── props ────────────────────────────────────────────────────────────────────

type Props = {
  employee: CurrentEmployee;
  leaveTypes: EligibleLeaveType[];
  submitting?: boolean;
  initialValues?: Partial<LeaveApplyRequest>;
  onSubmit: (payload: LeaveApplyRequest) => Promise<void> | void;
  onSaveDraft?: (payload: LeaveApplyRequest) => void;
};

// ─── component ────────────────────────────────────────────────────────────────

export function LeaveApplicationForm({
  employee,
  leaveTypes,
  submitting,
  initialValues,
  onSubmit,
  onSaveDraft,
}: Props) {
  const normalised = useMemo(() => leaveTypes.map(normaliseEligible), [leaveTypes]);

  // ── form state ──
  const [form, setForm] = useState<LeaveApplyRequest>({
    leaveTypeId: initialValues?.leaveTypeId ?? 0,
    startDate: initialValues?.startDate ?? "",
    endDate: initialValues?.endDate ?? "",
    reason: initialValues?.reason ?? "",
    attachmentPath: initialValues?.attachmentPath ?? "",
    reliefEmployeeId: initialValues?.reliefEmployeeId ?? null,
    delegateUserId: initialValues?.delegateUserId ?? null,
    delegateModules: initialValues?.delegateModules ?? [],
  });

  // ── relief employees ──
  const [reliefList, setReliefList] = useState<ReliefEmployeeOption[]>([]);
  const [loadingRelief, setLoadingRelief] = useState(false);
  const [reliefError, setReliefError] = useState<string | null>(null);
  const [onLeaveWarning, setOnLeaveWarning] = useState<string | null>(null);
  /** True when whoIsOnLeave returned 403 — list may include employees who are on leave */
  const [crossCheckLimited, setCrossCheckLimited] = useState(false);

  // ── relief employee search box ──
  const [reliefQuery, setReliefQuery] = useState("");
  const [reliefOpen, setReliefOpen] = useState(false);

  // ── approval delegation (Department Head / Managing Director only) ──
  const [delegationEligible, setDelegationEligible] = useState(false);
  const [availableModules, setAvailableModules] = useState<ApprovalModule[]>([]);
  const [delegateCandidates, setDelegateCandidates] = useState<DelegateCandidateOption[]>([]);
  const [delegateQuery, setDelegateQuery] = useState("");
  const [delegateOpen, setDelegateOpen] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const eligibility = await leaveApi.delegationEligibility();
        if (!active) return;
        setDelegationEligible(eligibility.eligible);
        setAvailableModules(eligibility.availableModules ?? []);
        if (eligibility.eligible) {
          const candidates = await leaveApi.delegationCandidates();
          if (active) setDelegateCandidates(candidates);
        }
      } catch {
        if (active) setDelegationEligible(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  // ── file upload ──
  const [upload, setUpload] = useState<UploadState>({ status: "idle" });
  const [dragOver, setDragOver] = useState(false);
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── derived ──
  const sel = useMemo(
    () => normalised.find((t) => t.id === form.leaveTypeId),
    [normalised, form.leaveTypeId],
  );
  const exWknd = sel?.excludeWeekends ?? true;
  const maxAllowed = useMemo(() => {
    if (!sel) return 0;
    const rem = Number(sel.remainingDays ?? 0);
    const maxPer = sel.maxDaysPerRequest ?? null;
    return sel.requiresBalance
      ? maxPer !== null
        ? Math.min(rem, Number(maxPer))
        : rem
      : maxPer !== null
        ? Number(maxPer)
        : 0;
  }, [sel]);
  const maxEnd = useMemo(
    () =>
      form.startDate && sel && maxAllowed > 0
        ? calcMaxEnd(form.startDate, maxAllowed, exWknd)
        : "",
    [form.startDate, sel, maxAllowed, exWknd],
  );
  const selDays = useMemo(
    () => countDays(form.startDate, form.endDate, exWknd),
    [form.startDate, form.endDate, exWknd],
  );
  const daysOk = selDays > 0 && (maxAllowed <= 0 || selDays <= maxAllowed);

  // Backdated/past applications (emergency leave filed after the fact) cannot offer a
  // relief employee or an approval delegate -- those only make sense for a current or
  // future-dated application.
  const isBackdated = useMemo(
    () => !!form.startDate && form.startDate < todayIso(),
    [form.startDate],
  );

  useEffect(() => {
    if (!isBackdated) return;
    setForm((p) => (p.reliefEmployeeId || p.delegateUserId || (p.delegateModules ?? []).length
      ? { ...p, reliefEmployeeId: null, delegateUserId: null, delegateModules: [] }
      : p));
  }, [isBackdated]);

  const selectedRelief = useMemo(
    () => reliefList.find((e) => e.employeeId === form.reliefEmployeeId) ?? null,
    [reliefList, form.reliefEmployeeId],
  );
  const filteredRelief = useMemo(() => {
    const q = reliefQuery.trim().toLowerCase();
    if (!q) return reliefList;
    return reliefList.filter((e) =>
      [e.fullName, e.employeeNumber, e.departmentName, e.jobTitle]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [reliefList, reliefQuery]);

  const selectedDelegate = useMemo(
    () => delegateCandidates.find((e) => e.userAccountId === form.delegateUserId) ?? null,
    [delegateCandidates, form.delegateUserId],
  );
  const filteredDelegates = useMemo(() => {
    const q = delegateQuery.trim().toLowerCase();
    if (!q) return delegateCandidates;
    return delegateCandidates.filter((e) =>
      [e.fullName, e.employeeNumber, e.departmentName, e.jobTitle]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [delegateCandidates, delegateQuery]);

  function toggleDelegateModule(module: ApprovalModule) {
    setForm((p) => {
      const current = p.delegateModules ?? [];
      const next = current.includes(module)
        ? current.filter((m) => m !== module)
        : [...current, module];
      return { ...p, delegateModules: next };
    });
  }

  // ── load relief employees when dates change ──
  // Cross-checks against whoIsOnLeave (best-effort — requires manager perm, silently degrades).
  useEffect(() => {
    async function load() {
      setReliefQuery("");
      if (!form.startDate || !form.endDate || form.endDate < form.startDate) {
        setReliefList([]);
        setOnLeaveWarning(null);
        setForm((p) => ({ ...p, reliefEmployeeId: null }));
        return;
      }
      try {
        setLoadingRelief(true);
        setReliefError(null);
        setOnLeaveWarning(null);
        setCrossCheckLimited(false);

        // Fetch available employees (backend should filter by date — we also double-check)
        const [rawList, onLeaveData] = await Promise.allSettled([
          leaveApi.availableReliefEmployees(form.startDate, form.endDate),
          leaveApi.whoIsOnLeave(),
        ]);

        const available = rawList.status === "fulfilled" ? rawList.value : [];

        if (rawList.status === "rejected") {
          setReliefError(getErrorMessage(rawList.reason));
          setReliefList([]);
          setForm((p) => ({ ...p, reliefEmployeeId: null }));
          return;
        }

        // If we got on-leave data, filter out employees on leave or official duty.
        // If whoIsOnLeave returned 403 (regular employee), mark limited so the UI can warn.
        let filtered = available;
        if (onLeaveData.status === "rejected") {
          setCrossCheckLimited(true);
        } else if (onLeaveData.status === "fulfilled") {
          // Build a set of employee IDs on leave during the requested period
          const leaveEntries = [
            ...(onLeaveData.value.currentlyOnLeave ?? []),
            ...(onLeaveData.value.pendingApproval  ?? []),
            ...(onLeaveData.value.upcomingLeave    ?? []),
          ];
          const onLeaveIds = new Set(
            leaveEntries
              .filter((e) => datesOverlap(e.startDate, e.endDate, form.startDate, form.endDate))
              .map((e) => e.employeeId),
          );

          // Build a set of employee IDs on official duty during the requested period
          const onDutyIds = new Set(
            (onLeaveData.value.onOfficialDuty ?? [])
              .filter((e) => datesOverlap(e.startDate, e.endDate, form.startDate, form.endDate))
              .map((e) => e.employeeId),
          );

          const unavailableIds = new Set([...onLeaveIds, ...onDutyIds]);

          if (unavailableIds.size > 0) {
            const onLeaveCount = available.filter((e) => onLeaveIds.has(e.employeeId)).length;
            const onDutyCount  = available.filter((e) => onDutyIds.has(e.employeeId) && !onLeaveIds.has(e.employeeId)).length;
            filtered = available.filter((e) => !unavailableIds.has(e.employeeId));

            const parts: string[] = [];
            if (onLeaveCount > 0)
              parts.push(`${onLeaveCount} on leave`);
            if (onDutyCount > 0)
              parts.push(`${onDutyCount} on official duty`);

            if (parts.length > 0) {
              const total = onLeaveCount + onDutyCount;
              setOnLeaveWarning(
                `${total} employee${total !== 1 ? "s" : ""} removed (${parts.join(", ")}) — unavailable during your selected dates.`,
              );
            }
          }
        }

        setReliefList(filtered);
        setForm((p) => ({
          ...p,
          reliefEmployeeId: filtered.some((e) => e.employeeId === p.reliefEmployeeId)
            ? p.reliefEmployeeId
            : null,
        }));
      } catch (err) {
        setReliefError(getErrorMessage(err));
        setReliefList([]);
        setForm((p) => ({ ...p, reliefEmployeeId: null }));
      } finally {
        setLoadingRelief(false);
      }
    }
    load();
  }, [form.startDate, form.endDate]);

  // clear attachment if leave type no longer requires it
  useEffect(() => {
    if (!sel?.requiresAttachment) {
      setUpload({ status: "idle" });
      setForm((p) => ({ ...p, attachmentPath: "" }));
    }
  }, [sel?.requiresAttachment]);

  // Default the leave type to Annual Leave once the eligible types load, unless a type was
  // already pre-selected (e.g. editing a draft) or the user has already picked one themselves.
  useEffect(() => {
    if (initialValues?.leaveTypeId || form.leaveTypeId || normalised.length === 0) return;
    const annual = normalised.find((t) => t.name.trim().toLowerCase() === "annual leave")
      ?? normalised.find((t) => t.name.trim().toLowerCase().includes("annual"));
    if (annual) selectType(annual.id);
  }, [normalised]);

  function set<K extends keyof LeaveApplyRequest>(k: K, v: LeaveApplyRequest[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function selectType(id: number) {
    set("leaveTypeId", id);
    set("startDate", "");
    set("endDate", "");
  }

  // ── file upload handlers ──
  async function handleFile(file: File) {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setUpload({ status: "error", message: "Only PDF, PNG, JPG/JPEG files are accepted." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUpload({ status: "error", message: "File must be under 5 MB." });
      return;
    }
    setUpload({ status: "uploading", name: file.name });
    try {
      const result = await leaveApi.uploadAttachment(file);
      setUpload({ status: "done", name: file.name, path: result.storagePath });
      set("attachmentPath", result.storagePath);
    } catch (err) {
      setUpload({ status: "error", message: getErrorMessage(err) });
      set("attachmentPath", "");
    }
  }

  function onFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function clearUpload() {
    setUpload({ status: "idle" });
    set("attachmentPath", "");
  }

  // ── submit ──
  function payload(): LeaveApplyRequest {
    return {
      leaveTypeId: form.leaveTypeId,
      startDate: form.startDate,
      endDate: form.endDate,
      reason: form.reason?.trim() || null,
      attachmentPath: form.attachmentPath?.trim() || null,
      reliefEmployeeId: !isBackdated ? (form.reliefEmployeeId ?? null) : null,
      delegateUserId: delegationEligible && !isBackdated ? (form.delegateUserId ?? null) : null,
      delegateModules:
        delegationEligible && !isBackdated && form.delegateUserId ? (form.delegateModules ?? []) : [],
    };
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.leaveTypeId) { toast.error("Please select a leave type."); return; }
    if (!form.startDate || !form.endDate) { toast.error("Please select start and end dates."); return; }
    if (form.endDate < form.startDate) { toast.error("End date cannot be before start date."); return; }
    if (sel?.requiresAttachment && !form.attachmentPath?.trim()) {
      toast.error("An attachment is required for this leave type.");
      return;
    }
    if (sel && maxAllowed > 0 && selDays > maxAllowed) {
      toast.error(`Maximum ${maxAllowed} day(s) allowed for this request.`);
      return;
    }
    if (!isBackdated && sel?.requiresReliefEmployee && !form.reliefEmployeeId) {
      toast.error("A relief employee is required for this leave type. Please select an available employee.");
      return;
    }
    if (delegationEligible && form.delegateUserId && (form.delegateModules ?? []).length === 0) {
      toast.error("Select at least one module to delegate, or clear the delegate you selected.");
      return;
    }
    await onSubmit(payload());
  }

  if (normalised.length === 0) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <AlertCircle className="h-5 w-5 shrink-0 text-amber-500" />
        <p className="text-sm font-semibold text-amber-800">
          No eligible leave types found for your profile. Contact HR.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4">

      {/* ── Balance chips — click to select leave type ─────────────── */}
      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">
          Select Leave Type
        </p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {normalised.map((t) => {
            const rem  = Number(t.remainingDays ?? 0);
            const ent  = Number(t.entitledDays ?? 0);
            const used = Number(t.usedDays ?? 0);
            const pend = Number(t.pendingDays ?? 0);
            const pct  = ent > 0 ? Math.min(100, Math.round((rem / ent) * 100)) : 100;
            const tone = rem <= 0 ? "red" : pct < 40 ? "amber" : "emerald";
            const barCls = { red: "bg-red-400", amber: "bg-amber-400", emerald: "bg-emerald-500" }[tone];
            const numCls = { red: "text-red-600", amber: "text-amber-700", emerald: "text-emerald-700" }[tone];
            const isSel  = form.leaveTypeId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => selectType(t.id)}
                className={`shrink-0 w-40 rounded-2xl border p-3 text-left transition-all focus:outline-none ${
                  isSel
                    ? "border-emerald-500 bg-emerald-50 shadow-sm ring-1 ring-emerald-300"
                    : "border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40"
                }`}
              >
                <div className="flex items-start justify-between gap-1">
                  <span className="line-clamp-2 text-xs font-bold leading-tight text-slate-700">
                    {t.name}
                  </span>
                  {t.applicableGender && t.applicableGender !== "ALL" && (
                    <span className={`shrink-0 rounded-full px-1 text-xs font-bold ${
                      t.applicableGender === "FEMALE" ? "bg-pink-100 text-pink-600" : "bg-blue-100 text-blue-600"
                    }`}>
                      {t.applicableGender === "FEMALE" ? "♀" : "♂"}
                    </span>
                  )}
                </div>

                <div className={`mt-2 text-2xl font-black ${numCls}`}>
                  {rem}
                  <span className="text-xs font-semibold text-slate-400">/{ent}d</span>
                </div>

                <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-200">
                  <div className={`h-full rounded-full ${barCls}`} style={{ width: `${pct}%` }} />
                </div>

                <div className="mt-1.5 flex justify-between text-xs text-slate-400">
                  <span>{used} used</span>
                  {pend > 0 && <span className="text-amber-600">{pend} pend.</span>}
                </div>

                {isSel && (
                  <div className="mt-1.5 flex items-center gap-1 text-xs font-bold text-emerald-600">
                    <CheckCircle2 className="h-3 w-3" /> Selected
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected type pills / rules ─────────────────────────────── */}
      {sel && (
        <div className="flex flex-wrap gap-2 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-2.5 text-xs text-slate-600">
          <span className="font-bold text-emerald-800">{sel.name}</span>
          {sel.requiresAttachment && <span className="font-semibold text-amber-700">· 📎 Attachment required</span>}
          {sel.requiresReliefEmployee && <span className="font-semibold text-blue-700">· 👥 Relief required</span>}
        </div>
      )}

      {/* ── Main form card ────────────────────────────────────────── */}
      <div className="rounded-3xl border bg-white shadow-sm">

        {/* Dates */}
        <div className="border-b p-5">
          <SectionHead title="Dates" />

          <label className="mt-3 block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">
              Leave Type <span className="text-red-500">*</span>
            </span>
            <select
              className="input"
              value={form.leaveTypeId || ""}
              onChange={(e) => selectType(Number(e.target.value))}
            >
              <option value="" disabled>Select a leave type…</option>
              {normalised.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({Number(t.remainingDays ?? 0)}/{Number(t.entitledDays ?? 0)}d remaining)
                </option>
              ))}
            </select>
          </label>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="space-y-1.5">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <CalendarDays className="h-3.5 w-3.5 text-slate-400" /> Start Date
                <span className="text-red-500">*</span>
              </span>
              <input
                type="date"
                className="input"
                value={form.startDate}
                disabled={!form.leaveTypeId}
                onChange={(e) => { set("startDate", e.target.value); set("endDate", ""); }}
                required
              />
            </label>
            <label className="space-y-1.5">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <CalendarDays className="h-3.5 w-3.5 text-slate-400" /> End Date
                <span className="text-red-500">*</span>
              </span>
              <input
                type="date"
                className="input"
                value={form.endDate}
                min={form.startDate || undefined}
                max={maxEnd || undefined}
                disabled={!form.startDate}
                onChange={(e) => set("endDate", e.target.value)}
                required
              />
              {maxEnd && <p className="text-xs text-slate-400">Latest allowed: {fmt(maxEnd)}</p>}
            </label>
          </div>

          {form.startDate && form.endDate && (
            <div className={`mt-4 flex items-center gap-3 rounded-xl px-4 py-3 ${
              daysOk ? "border border-emerald-200 bg-emerald-50" : "border border-red-200 bg-red-50"
            }`}>
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl font-black ${
                daysOk ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
              }`}>
                {selDays}
              </div>
              <div>
                <div className={`font-bold text-sm ${daysOk ? "text-emerald-800" : "text-red-800"}`}>
                  {selDays} working day{selDays !== 1 ? "s" : ""} selected
                </div>
                <div className="text-xs text-slate-500">
                  {fmt(form.startDate)} → {fmt(form.endDate)}
                  {maxAllowed > 0 ? ` · Max: ${maxAllowed}d` : ""}
                </div>
                {!daysOk && selDays > 0 && (
                  <div className="mt-0.5 text-xs font-bold text-red-700">
                    Exceeds maximum of {maxAllowed} days.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Relief Employee */}
        <div className="border-b p-5">
          <SectionHead
            title="Relief Employee"
            optional={!sel?.requiresReliefEmployee}
          />
          {sel?.requiresReliefEmployee && !isBackdated && (
            <p className="mt-0.5 mb-3 text-xs text-slate-500">
              Required — select who covers your duties.
            </p>
          )}
          {sel?.requiresReliefEmployee && isBackdated && (
            <p className="mt-0.5 mb-3 text-xs text-slate-500">
              Waived for this backdated application.
            </p>
          )}

          {isBackdated ? (
            <p className="text-sm text-amber-700">
              Not available for a backdated application -- a relief employee can only be
              assigned for a leave application starting today or later.
            </p>
          ) : !form.startDate || !form.endDate ? (
            <p className="text-sm text-slate-400">Select your leave dates first.</p>
          ) : loadingRelief ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Clock className="h-4 w-4 animate-spin" /> Loading available employees…
            </div>
          ) : (
            <div className="relative">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  className="input pl-8 pr-7"
                  placeholder={
                    reliefList.length === 0
                      ? "No available employees for these dates"
                      : "Search by name, number, or department…"
                  }
                  value={selectedRelief ? selectedRelief.fullName : reliefQuery}
                  onChange={(e) => {
                    if (form.reliefEmployeeId) set("reliefEmployeeId", null);
                    setReliefQuery(e.target.value);
                    setReliefOpen(true);
                  }}
                  onFocus={() => setReliefOpen(true)}
                  onBlur={() => setTimeout(() => setReliefOpen(false), 150)}
                />
                {(selectedRelief || reliefQuery) && (
                  <button
                    type="button"
                    onMouseDown={() => {
                      set("reliefEmployeeId", null);
                      setReliefQuery("");
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {reliefOpen && !selectedRelief && (
                <div className="absolute z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onMouseDown={() => { set("reliefEmployeeId", null); setReliefQuery(""); setReliefOpen(false); }}
                    className="block w-full px-3 py-1.5 text-left text-sm text-slate-500 hover:bg-emerald-50"
                  >
                    None — no relief needed
                  </button>
                  {filteredRelief.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-slate-400">
                      {reliefQuery.trim() ? "No matching employees" : "No available employees for these dates"}
                    </p>
                  ) : (
                    <>
                      {filteredRelief.map((e) => (
                        <button
                          key={e.employeeId}
                          type="button"
                          onMouseDown={() => {
                            set("reliefEmployeeId", e.employeeId);
                            setReliefQuery("");
                            setReliefOpen(false);
                          }}
                          className="block w-full px-3 py-1.5 text-left text-sm hover:bg-emerald-50"
                        >
                          <span className="font-medium text-slate-700">{e.fullName}</span>
                          {e.employeeNumber && <span className="ml-1.5 text-xs text-slate-400">({e.employeeNumber})</span>}
                          {(e.departmentName || e.jobTitle) && (
                            <span className="block text-xs text-slate-400">
                              {[e.departmentName, e.jobTitle].filter(Boolean).join(" — ")}
                            </span>
                          )}
                        </button>
                      ))}
                      <p className="border-t px-3 pt-1.5 text-[11px] text-slate-400">
                        {filteredRelief.length} of {reliefList.length} available employee{reliefList.length !== 1 ? "s" : ""}
                        {reliefQuery.trim() ? " matching" : " shown"}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {reliefError && <p className="mt-1 text-xs font-semibold text-red-600">{reliefError}</p>}
          {onLeaveWarning && (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs font-semibold text-amber-700">
              <span className="mt-px shrink-0">⚠</span>{onLeaveWarning}
            </p>
          )}
          {crossCheckLimited && !onLeaveWarning && (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-500">
              <span className="mt-px shrink-0">ℹ</span>
              Availability is based on your dates only. If your chosen relief employee is on leave, the application will be flagged during review.
            </p>
          )}

          {form.reliefEmployeeId && reliefList.length > 0 && (() => {
            const emp = reliefList.find((e) => e.employeeId === form.reliefEmployeeId);
            if (!emp) return null;
            return (
              <div className="mt-3 flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-200 text-sm font-black text-blue-800">
                  {emp.fullName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-bold text-blue-900 text-sm">{emp.fullName}</div>
                  <div className="text-xs text-blue-600">
                    {[emp.departmentName, emp.jobTitle].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <CheckCircle2 className="ml-auto h-5 w-5 text-blue-500" />
              </div>
            );
          })()}
        </div>

        {/* Delegate My Approvals (Department Heads / Managing Director only) */}
        {delegationEligible && isBackdated && (
          <div className="border-b p-5">
            <SectionHead title="Delegate My Approvals" optional />
            <p className="mt-1 text-sm text-amber-700">
              Not available for a backdated application -- approval delegation can only be
              set up for a leave application starting today or later.
            </p>
          </div>
        )}

        {delegationEligible && !isBackdated && (
          <div className="border-b p-5">
            <SectionHead title="Delegate My Approvals" optional />

            <div className="relative mt-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  className="input pl-8 pr-7"
                  placeholder="Search by name, number, or department…"
                  value={selectedDelegate ? selectedDelegate.fullName : delegateQuery}
                  onChange={(e) => {
                    if (form.delegateUserId) set("delegateUserId", null);
                    setDelegateQuery(e.target.value);
                    setDelegateOpen(true);
                  }}
                  onFocus={() => setDelegateOpen(true)}
                  onBlur={() => setTimeout(() => setDelegateOpen(false), 150)}
                />
                {(selectedDelegate || delegateQuery) && (
                  <button
                    type="button"
                    onMouseDown={() => {
                      set("delegateUserId", null);
                      set("delegateModules", []);
                      setDelegateQuery("");
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              {delegateOpen && !selectedDelegate && (
                <div className="absolute z-20 mt-1 max-h-80 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onMouseDown={() => { set("delegateUserId", null); setDelegateQuery(""); setDelegateOpen(false); }}
                    className="block w-full px-3 py-1.5 text-left text-sm text-slate-500 hover:bg-emerald-50"
                  >
                    None — don't delegate approvals
                  </button>
                  {filteredDelegates.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-slate-400">
                      {delegateQuery.trim() ? "No matching employees" : "No other employees available"}
                    </p>
                  ) : (
                    filteredDelegates.map((e) => (
                      <button
                        key={e.userAccountId}
                        type="button"
                        onMouseDown={() => {
                          set("delegateUserId", e.userAccountId);
                          setDelegateQuery("");
                          setDelegateOpen(false);
                        }}
                        className="block w-full px-3 py-1.5 text-left text-sm hover:bg-emerald-50"
                      >
                        <span className="font-medium text-slate-700">{e.fullName}</span>
                        {e.employeeNumber && <span className="ml-1.5 text-xs text-slate-400">({e.employeeNumber})</span>}
                        {(e.departmentName || e.jobTitle) && (
                          <span className="block text-xs text-slate-400">
                            {[e.departmentName, e.jobTitle].filter(Boolean).join(" — ")}
                          </span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {form.delegateUserId && availableModules.length === 0 && (
              <p className="mt-3 text-xs font-semibold text-amber-700">
                You're not currently set up as an approver for any module, so there's nothing
                to delegate -- clear the delegate above.
              </p>
            )}

            {form.delegateUserId && availableModules.length > 0 && (
              <div className="mt-4">
                <p className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-400">
                  Modules to delegate
                </p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {MODULE_ORDER.filter((module) => availableModules.includes(module)).map((module) => {
                    const checked = (form.delegateModules ?? []).includes(module);
                    return (
                      <label
                        key={module}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                          checked
                            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                            : "border-slate-200 text-slate-700 hover:border-emerald-200 hover:bg-slate-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDelegateModule(module)}
                          className="h-5 w-5 shrink-0 accent-emerald-600"
                        />
                        {MODULE_LABELS[module]}
                      </label>
                    );
                  })}
                </div>
                {(form.delegateModules ?? []).length === 0 && (
                  <p className="mt-2 text-xs font-semibold text-amber-700">
                    Select at least one module, or clear the delegate above.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Attachment (conditional) */}
        {sel?.requiresAttachment && (
          <div className="border-b p-5">
            <SectionHead title="Attachment" />
            <p className="mt-0.5 mb-3 text-xs text-amber-700">
              Required for this leave type. Upload a PDF, PNG, or JPG/JPEG (max 5 MB).
            </p>

            {upload.status === "done" ? (
              <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100">
                  <FileText className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{upload.name}</p>
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Uploaded successfully
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearUpload}
                  className="rounded-xl p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ) : upload.status === "uploading" ? (
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-700">{upload.name}</p>
                  <p className="text-xs text-slate-400">Uploading…</p>
                </div>
              </div>
            ) : (
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`cursor-pointer rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
                  dragOver
                    ? "border-emerald-400 bg-emerald-50"
                    : "border-amber-300 bg-amber-50/30 hover:border-emerald-400 hover:bg-emerald-50/40"
                }`}
              >
                <UploadCloud className={`mx-auto h-8 w-8 mb-2 ${dragOver ? "text-emerald-500" : "text-amber-400"}`} />
                <p className="text-sm font-semibold text-slate-700">
                  {dragOver ? "Drop file here" : "Click or drag file to upload"}
                </p>
                <p className="mt-1 text-xs text-slate-400">PDF, PNG, JPG, JPEG — max 5 MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={ACCEPTED_EXT}
                  onChange={onFileInput}
                />
              </div>
            )}

            {upload.status === "error" && (
              <div className="mt-2 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                <X className="h-3.5 w-3.5 shrink-0" />
                {upload.message}
                <button type="button" onClick={clearUpload} className="ml-auto underline">Try again</button>
              </div>
            )}
          </div>
        )}

        {/* Reason */}
        <div className="p-5">
          <SectionHead title="Reason" optional />
          <textarea
            className="mt-3 input min-h-24 resize-y"
            value={form.reason ?? ""}
            onChange={(e) => set("reason", e.target.value)}
            placeholder="Briefly describe your reason for leave…"
          />
        </div>
      </div>

      {/* ── Footer actions ────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-5 py-4 shadow-sm">
        <div className="text-xs text-slate-500">
          {sel && form.startDate && form.endDate && selDays > 0 ? (
            <span>
              <strong className="text-slate-800">{sel.name}</strong>
              {" · "}
              <strong className="text-emerald-700">{selDays}</strong> day{selDays !== 1 ? "s" : ""}{" · "}
              {fmt(form.startDate)} → {fmt(form.endDate)}
            </span>
          ) : !form.leaveTypeId ? (
            "Select a leave type above to get started."
          ) : (
            "Pick your leave dates above."
          )}
        </div>
        <div className="flex gap-2">
          {onSaveDraft && (
            <button
              type="button"
              onClick={() => onSaveDraft(payload())}
              className="rounded-xl border px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              Save Draft
            </button>
          )}
          <button
            type="submit"
            disabled={
              submitting ||
              !form.leaveTypeId ||
              !form.startDate ||
              !form.endDate ||
              !daysOk ||
              (sel?.requiresAttachment && upload.status !== "done") ||
              (!isBackdated && sel?.requiresReliefEmployee === true && !form.reliefEmployeeId)
            }
            className="rounded-xl bg-emerald-700 px-6 py-2.5 text-sm font-bold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Submitting…" : "Submit Application"}
          </button>
        </div>
      </div>

    </form>
  );
}

// ─── SectionHead ──────────────────────────────────────────────────────────────

function SectionHead({ title, optional }: { title: string; optional?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <h3 className="font-black text-slate-900">{title}</h3>
      {optional && <span className="text-xs text-slate-400">optional</span>}
    </div>
  );
}
